// CalculatorPopover.tsx — wissenschaftlicher Taschenrechner als Popover.
//
// Helfer neben den Kennzahlen-Feldern: schnell „1.264.600/12" rechnen und das
// Ergebnis per „Kopieren" in eine Euro-Zelle einfügen. Gerechnet wird in
// `evaluateExpression` (utils/calcExpression) — hier liegt nur die Bedienung.
//
// Zwei bewusste Entscheidungen:
//
//  1. Das Display ist ein ECHTES `<input>` und NICHT readOnly wie bei einem
//     Tischrechner. Damit funktionieren Tastatur, Markieren, Einfügen und der
//     Cursor ohne eigenen Nachbau; die Tasten schreiben nur an der Cursor-
//     position mit (Muster: telephony/Dialpad, inkl. Fokus-Rücksprung).
//  2. Die Sondertasten (x², √, 1/x, ±) sind reine STRING-Umformungen des
//     Ausdrucks, keine Parser-Features. Der Parser bleibt dadurch klein — und
//     was eine Taste getan hat, steht danach sichtbar im Feld.
//
// STYLING: Das Popover rendert per Portal an `document.body` und liegt damit
// AUSSERHALB des FluentProviders — `tokens.*` lösen dort zu leeren Strings auf.
// Deshalb durchgehend REFINED-Literale (Optik an `.tp-dp-key` angelehnt).
//
// Bewusst NICHT im Umfang: eine Übernahme des Ergebnisses in eine Tabellenzelle.
// Der Rechner kennt kein Zielfeld; „Kopieren" reicht dafür.

import * as React from "react";
import { makeStyles, mergeClasses, shorthands } from "@fluentui/react-components";
import { Calculator20Regular } from "@fluentui/react-icons";
import { RdPopover } from "../redesign/table";
import { REFINED } from "../risikoanalyseStep/tokens";
import { evaluateExpression } from "../../utils/calcExpression";
import { aktivesDokument } from "../windowManager/browserFenster/fensterRegister";

// ---------- Zahlformat ----------------------------------------------------

/** Schneidet die Fließkomma-Artefakte ab (0,1+0,2 → 0,30000000000000004). */
const round = (v: number): number => Number(v.toPrecision(12));

/** Anzeige: deutsches Format MIT Tausenderpunkten. */
const fmtDisplay = (v: number): string =>
  round(v).toLocaleString("de-DE", { maximumFractionDigits: 10 });

/**
 * Rohwert für Zwischenablage und „="-Übernahme: deutsches Format OHNE
 * Tausenderpunkte („1264600" bzw. „1264600,5"). Die Punkte wären beim Einfügen
 * in eine Euro-Zelle nur Ballast — und beim Zurücklesen in den eigenen Parser
 * sogar zweideutig, denn dort ist der Punkt der Tausendertrenner.
 */
const fmtRaw = (v: number): string =>
  round(v).toLocaleString("de-DE", { maximumFractionDigits: 10, useGrouping: false });

// ---------- Eingabe-Filter ------------------------------------------------

// Die „hübschen" Zeichen der Tasten (×, ÷, −) werden auf ihre ASCII-Pendants
// normalisiert, damit auch Eingefügtes aus anderen Programmen rechnet.
const normalize = (s: string): string =>
  s.replace(/[×·]/g, "*").replace(/÷/g, "/").replace(/[−–—]/g, "-");

// Erlaubt: Ziffern, Buchstaben (sin/cos/tan/ln/log/sqrt/pi/e), π, Rechenzeichen,
// Klammern, Trennzeichen, Leerraum. Alles andere fällt still raus.
const ALLOWED = /[0-9a-zA-Zπ,.+\-*/^()%\s]/;
const sanitize = (s: string): string =>
  Array.from(normalize(s))
    .filter((ch) => ALLOWED.test(ch))
    .join("");

/**
 * Ob die bei `von` geöffnete Klammer erst am Ende des Strings schließt — also ob
 * der Ausdruck als Ganzes geklammert ist. „-(1+2)" ja, „-(1+2)*(3)" nein.
 */
const klammertGanz = (s: string, von: number): boolean => {
  let tiefe = 0;
  for (let i = von; i < s.length; i += 1) {
    if (s[i] === "(") tiefe += 1;
    else if (s[i] === ")") {
      tiefe -= 1;
      if (tiefe === 0) return i === s.length - 1;
    }
  }
  return false;
};

// ---------- Tastenbeschriftungen ------------------------------------------

/** Wissenschaftliche Tasten, die schlicht Text einfügen (Reihenfolge = Raster). */
const SCI_KEYS: { label: string; ins: string; title: string }[] = [
  { label: "sin", ins: "sin(", title: "Sinus (Bogenmaß)" },
  { label: "cos", ins: "cos(", title: "Kosinus (Bogenmaß)" },
  { label: "tan", ins: "tan(", title: "Tangens (Bogenmaß)" },
  { label: "ln", ins: "ln(", title: "Natürlicher Logarithmus" },
  { label: "log", ins: "log(", title: "Zehnerlogarithmus" },
  { label: "x²", ins: "^2", title: "Quadrieren" },
  { label: "xʸ", ins: "^", title: "Potenz" },
  { label: "√", ins: "sqrt(", title: "Quadratwurzel" },
  { label: "π", ins: "π", title: "Kreiszahl" },
  { label: "e", ins: "e", title: "Eulersche Zahl" },
  { label: "(", ins: "(", title: "Klammer auf" },
  { label: ")", ins: ")", title: "Klammer zu" },
];

// ---------- Styles --------------------------------------------------------

const useStyles = makeStyles({
  panel: {
    padding: "12px",
    display: "flex",
    flexDirection: "column",
    gap: "9px",
    fontFamily: REFINED.fontUI,
    color: REFINED.ink,
  },
  display: {
    border: `1px solid ${REFINED.line}`,
    borderRadius: "12px",
    background: REFINED.white,
    padding: "7px 10px 5px",
  },
  displayTop: { display: "flex", alignItems: "center", gap: "6px" },
  memFlag: {
    flex: "0 0 auto",
    padding: "2px 5px",
    borderRadius: "6px",
    background: REFINED.aub50,
    color: REFINED.aub,
    fontSize: `${REFINED.fs.xxs}px`,
    fontWeight: 800,
    lineHeight: 1,
  },
  input: {
    flex: "1 1 auto",
    minWidth: 0,
    border: "none",
    outline: "none",
    background: "transparent",
    padding: "4px 0",
    fontFamily: REFINED.fontUI,
    fontSize: `${REFINED.fs.lg}px`,
    fontWeight: 700,
    color: REFINED.ink,
    fontVariantNumeric: "tabular-nums",
    "::placeholder": { fontWeight: 500, color: REFINED.ink3 },
  },
  preview: {
    minHeight: "17px",
    textAlign: "right",
    fontSize: `${REFINED.fs.md}px`,
    fontWeight: 700,
    color: REFINED.aub,
    fontVariantNumeric: "tabular-nums",
  },
  error: {
    fontSize: `${REFINED.fs.xs}px`,
    fontWeight: 700,
    color: REFINED.err,
    padding: "0 2px",
  },
  grid: { display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: "6px" },
  key: {
    minHeight: "36px",
    display: "grid",
    placeItems: "center",
    border: `1px solid ${REFINED.line}`,
    borderRadius: "10px",
    background: REFINED.white,
    color: REFINED.ink,
    fontFamily: REFINED.fontUI,
    fontSize: `${REFINED.fs.md}px`,
    fontWeight: 700,
    cursor: "pointer",
    transition: "background .1s, border-color .1s, transform .04s",
    ":hover": { background: REFINED.aub50, ...shorthands.borderColor(REFINED.aub200), color: REFINED.aub },
    ":active": { transform: "scale(.95)" },
    ":disabled": {
      background: REFINED.white,
      ...shorthands.borderColor(REFINED.line),
      color: REFINED.ink3,
      opacity: 0.45,
      cursor: "not-allowed",
    },
    ":disabled:hover": { background: REFINED.white, ...shorthands.borderColor(REFINED.line), color: REFINED.ink3 },
  },
  // Speicher- und Funktionstasten: kleiner gesetzt, ruhigere Fläche.
  keySoft: {
    background: REFINED.fieldBg,
    color: REFINED.ink2,
    fontSize: `${REFINED.fs.sm}px`,
  },
  keyWide: { gridColumn: "span 2" },
  keyEq: {
    gridColumn: "span 2",
    background: REFINED.gold,
    ...shorthands.borderColor(REFINED.gold),
    color: REFINED.aubDeep,
    fontWeight: 800,
    boxShadow: "0 3px 10px -4px rgba(240,160,32,.8)",
    ":hover": {
      background: REFINED.gold,
      ...shorthands.borderColor(REFINED.gold),
      color: REFINED.aubDeep,
      filter: "brightness(1.05)",
    },
  },
  foot: { display: "flex", alignItems: "center", gap: "8px" },
  footHint: {
    flex: "1 1 auto",
    minWidth: 0,
    fontSize: `${REFINED.fs.xs}px`,
    fontWeight: 600,
    color: REFINED.ink3,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  copyBtn: {
    flex: "0 0 auto",
    border: `1.5px solid ${REFINED.line}`,
    borderRadius: "10px",
    background: REFINED.white,
    color: REFINED.ink,
    padding: "8px 13px",
    fontFamily: REFINED.fontUI,
    fontSize: `${REFINED.fs.sm}px`,
    fontWeight: 700,
    cursor: "pointer",
    transition: "background .12s, border-color .12s, color .12s",
    ":hover": { background: REFINED.aub50, ...shorthands.borderColor(REFINED.aub200), color: REFINED.aub },
    ":disabled": { opacity: 0.45, cursor: "not-allowed", background: REFINED.white, color: REFINED.ink3 },
  },
  copyBtnDone: {
    background: REFINED.okBg,
    ...shorthands.borderColor(REFINED.ok),
    color: REFINED.ok,
    ":hover": { background: REFINED.okBg, ...shorthands.borderColor(REFINED.ok), color: REFINED.ok },
  },
  trigger: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    border: `1.5px solid ${REFINED.line}`,
    borderRadius: "10px",
    background: REFINED.white,
    color: REFINED.ink,
    padding: "9px 14px",
    fontFamily: REFINED.fontUI,
    fontSize: `${REFINED.fs.sm}px`,
    fontWeight: 700,
    cursor: "pointer",
    whiteSpace: "nowrap",
    transition: "background .12s, border-color .12s, color .12s",
    ":hover": { background: REFINED.aub50, ...shorthands.borderColor(REFINED.aub200), color: REFINED.aub },
  },
  triggerOn: { background: REFINED.aub50, ...shorthands.borderColor(REFINED.aub200), color: REFINED.aub },
});

/** Eine Taste. `variant` steuert ausschließlich die Optik. */
const Key: React.FC<{
  label: React.ReactNode;
  onPress: () => void;
  variant?: "soft" | "wide" | "eq";
  title?: string;
  ariaLabel?: string;
  disabled?: boolean;
}> = ({ label, onPress, variant, title, ariaLabel, disabled }) => {
  const s = useStyles();
  return (
    <button
      type="button"
      className={mergeClasses(
        s.key,
        variant === "soft" && s.keySoft,
        variant === "wide" && s.keyWide,
        variant === "eq" && s.keyEq,
      )}
      onClick={onPress}
      title={title}
      aria-label={ariaLabel ?? title}
      disabled={disabled}
    >
      {label}
    </button>
  );
};

// ---------- Komponente ----------------------------------------------------

export interface CalculatorPopoverProps {
  /**
   * Eigener Auslöser (z. B. ein Icon-Button in einer Toolbar). Ohne Angabe
   * rendert die Komponente ihren Standard-Button „Rechner".
   */
  trigger?: (p: {
    open: boolean;
    toggle: () => void;
    ref: React.Ref<HTMLButtonElement>;
  }) => React.ReactNode;
}

export const CalculatorPopover: React.FC<CalculatorPopoverProps> = ({ trigger }) => {
  const s = useStyles();
  const [expr, setExpr] = React.useState("");
  const [memory, setMemory] = React.useState<number | null>(null);
  // Fehler NUR nach „=" bzw. Enter: während des Tippens ist jeder halbe Ausdruck
  // („12*") kurz ungültig — eine rote Meldung je Zeichen wäre reiner Lärm.
  const [error, setError] = React.useState<string | null>(null);
  const [copied, setCopied] = React.useState(false);

  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const caretRef = React.useRef<number | null>(null);

  const live = React.useMemo(() => evaluateExpression(expr), [expr]);
  const result = live.ok ? live.value : null;

  // Autofokus beim Öffnen: der Portal-Inhalt wird erst dann gemountet, der
  // Callback-Ref feuert also genau zum richtigen Zeitpunkt — kein „open"-Flag.
  const setInputRef = React.useCallback((el: HTMLInputElement | null) => {
    inputRef.current = el;
    el?.focus();
  }, []);

  // Beim gesteuerten Input springt der Cursor nach einem programmatischen
  // Wertwechsel ans Ende — hier wird die gemerkte Position wiederhergestellt.
  React.useLayoutEffect(() => {
    const caret = caretRef.current;
    if (caret === null) return;
    caretRef.current = null;
    inputRef.current?.setSelectionRange(caret, caret);
  });

  React.useEffect(() => {
    if (!copied) return;
    const t = window.setTimeout(() => setCopied(false), 1400);
    return () => window.clearTimeout(t);
  }, [copied]);

  /** Ausdruck + Cursor in einem Schritt setzen und den Fokus zurückholen. */
  const apply = React.useCallback((next: string, caret: number) => {
    setExpr(next);
    setError(null);
    setCopied(false);
    caretRef.current = caret;
    inputRef.current?.focus();
  }, []);

  /** Text an der Cursorposition einfügen (ersetzt eine Auswahl). */
  const insert = React.useCallback(
    (text: string) => {
      const el = inputRef.current;
      const start = el?.selectionStart ?? expr.length;
      const end = el?.selectionEnd ?? expr.length;
      apply(expr.slice(0, start) + text + expr.slice(end), start + text.length);
    },
    [expr, apply],
  );

  const backspace = React.useCallback(() => {
    const el = inputRef.current;
    const start = el?.selectionStart ?? expr.length;
    const end = el?.selectionEnd ?? expr.length;
    if (start !== end) apply(expr.slice(0, start) + expr.slice(end), start);
    else if (start > 0) apply(expr.slice(0, start - 1) + expr.slice(start), start - 1);
  }, [expr, apply]);

  /**
   * CE — löscht nur die zuletzt getippte Zahl statt des ganzen Ausdrucks:
   * „1200*13," wird zu „1200*". Endet der Ausdruck nicht auf einer Zahl, fällt
   * CE auf ein einzelnes Zeichen zurück. Nur per Entf-Taste erreichbar; auf dem
   * Raster liegt das gröbere „C".
   */
  const clearEntry = React.useCallback(() => {
    const m = /[\d.,]+$/.exec(expr);
    const next = m ? expr.slice(0, expr.length - m[0].length) : expr.slice(0, -1);
    apply(next, next.length);
  }, [expr, apply]);

  /** 1/x klammert den GANZEN Ausdruck: „12+4" wird zu „1/(12+4)", nicht „12+1/4". */
  const reciprocal = React.useCallback(() => {
    const t = expr.trim();
    if (!t) {
      insert("1/(");
      return;
    }
    const next = `1/(${t})`;
    apply(next, next.length);
  }, [expr, apply, insert]);

  /**
   * ± kippt das Vorzeichen des GESAMTEN Ausdrucks — wie 1/x wird dafür geklammert:
   * „12+4" wird zu „-(12+4)" = −16. Ein bloß vorangestelltes Minus ergäbe
   * „-12+4" = −8, also das Vorzeichen nur des ersten Summanden. Eine einzelne
   * Zahl braucht die Klammer nicht.
   */
  const negate = React.useCallback(() => {
    const t = expr.trim();
    if (!t) return;
    if (t.startsWith("-(") && klammertGanz(t, 1)) {
      const next = t.slice(2, -1);
      apply(next, next.length);
      return;
    }
    if (t.startsWith("-")) {
      const next = t.slice(1);
      apply(next, next.length);
      return;
    }
    const next = /^[\d.,]+$/.test(t) ? `-${t}` : `-(${t})`;
    apply(next, next.length);
  }, [expr, apply]);

  /**
   * „=" / Enter: rechnet und schreibt das Ergebnis als Rohwert ins Feld, damit
   * sich direkt damit weiterrechnen lässt. Nur hier wird ein Fehler angezeigt.
   */
  const submit = React.useCallback(() => {
    if (!expr.trim()) return;
    const res = evaluateExpression(expr);
    if (!res.ok) {
      setError(res.error);
      inputRef.current?.focus();
      return;
    }
    const next = fmtRaw(res.value);
    apply(next, next.length);
  }, [expr, apply]);

  const memoryStore = (mode: "set" | "add" | "sub") => {
    if (result === null) return;
    setMemory((m) =>
      mode === "set" ? round(result) : round((m ?? 0) + (mode === "add" ? result : -result)),
    );
    inputRef.current?.focus();
  };

  const copy = async () => {
    if (result === null) return;
    const text = fmtRaw(result);
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        // Fallback ohne Clipboard-API (unsicherer Kontext/älterer Browser):
        // unsichtbares Textfeld + execCommand.
        const dok = aktivesDokument();
        const ta = dok.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        dok.body.appendChild(ta);
        ta.select();
        dok.execCommand("copy");
        ta.remove();
      }
      setCopied(true);
    } catch (e) {
      console.error("Kopieren fehlgeschlagen", e);
    }
    inputRef.current?.focus();
  };

  // Liegt auf dem Panel (nicht nur auf dem Input), damit die Tasten auch dann
  // greifen, wenn der Fokus gerade auf einer Taste steht.
  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Escape") {
      // KONFLIKT: RdPopover lauscht global (window) auf Escape und schließt
      // sich. Ein halb getippter Ausdruck soll das Popover aber nicht kosten —
      // solange etwas im Feld steht, fängt Escape hier ab und leert nur die
      // Eingabe. `stopPropagation` hält das native Event am Portal-Container
      // (document.body) auf, bevor es den window-Listener erreicht. Ist das
      // Feld leer, läuft das Event durch und Escape schließt wie überall sonst.
      if (expr) {
        e.stopPropagation();
        apply("", 0);
      }
      return;
    }
    if (e.key === "Enter" || e.key === "=") {
      // Enter auf einer Taste MUSS die Taste auslösen: der Handler hängt am
      // Panel, also am Vorfahren aller Buttons — `preventDefault` unterdrückte
      // sonst deren Click und der Tastendruck rechnete, statt die „7"
      // einzufügen (dasselbe galt für „Kopieren"). „=" hat auf einem Button
      // keine eigene Bedeutung und gilt weiterhin dem Ausdruck.
      if (e.key === "Enter" && (e.target as HTMLElement).tagName === "BUTTON") return;
      e.preventDefault();
      submit();
      return;
    }
    if (e.key === "Delete") {
      e.preventDefault();
      clearEntry();
      return;
    }
    if (e.key === ".") {
      // Numpad-Punkt als Dezimaltrenner: deutsche Layouts liefern hier oft „.",
      // im Ausdruck ist der Punkt aber der TAUSENDER-Trenner — „1.5" wäre 15
      // statt 1,5. Umgebogen wird nur die TASTE; Eingefügtes („1.200.000")
      // behält seine Punkte und bleibt damit korrekt.
      e.preventDefault();
      insert(",");
    }
  };

  const previewText = expr.trim() && live.ok ? `= ${fmtDisplay(live.value)}` : " ";

  return (
    <RdPopover
      width={300}
      align="end"
      trigger={
        trigger ??
        (({ open, toggle, ref }) => (
          <button
            ref={ref}
            type="button"
            className={mergeClasses(s.trigger, open && s.triggerOn)}
            onClick={toggle}
            title="Rechner"
          >
            <Calculator20Regular />
            Rechner
          </button>
        ))
      }
    >
      {() => (
        <div className={s.panel} onKeyDown={onKeyDown}>
          <div className={s.display}>
            <div className={s.displayTop}>
              {memory !== null && (
                <span className={s.memFlag} title={`Speicher: ${fmtDisplay(memory)}`}>
                  M
                </span>
              )}
              <input
                ref={setInputRef}
                className={s.input}
                value={expr}
                onChange={(e) => {
                  setExpr(sanitize(e.target.value));
                  setError(null);
                  setCopied(false);
                }}
                placeholder="0"
                aria-label="Rechenausdruck"
                autoComplete="off"
                spellCheck={false}
              />
            </div>
            <div className={s.preview} aria-live="polite">
              {previewText}
            </div>
          </div>

          {error && <div className={s.error}>{error}</div>}

          <div className={s.grid}>
            <Key
              label="MC"
              variant="soft"
              title="Speicher löschen"
              disabled={memory === null}
              onPress={() => {
                setMemory(null);
                inputRef.current?.focus();
              }}
            />
            <Key
              label="MR"
              variant="soft"
              title="Speicher einfügen"
              disabled={memory === null}
              onPress={() => memory !== null && insert(fmtRaw(memory))}
            />
            <Key
              label="M+"
              variant="soft"
              title="Ergebnis zum Speicher addieren"
              disabled={result === null}
              onPress={() => memoryStore("add")}
            />
            <Key
              label="M−"
              variant="soft"
              title="Ergebnis vom Speicher abziehen"
              disabled={result === null}
              onPress={() => memoryStore("sub")}
            />
            <Key
              label="MS"
              variant="soft"
              title="Ergebnis im Speicher ablegen"
              disabled={result === null}
              onPress={() => memoryStore("set")}
            />
          </div>

          <div className={s.grid}>
            {SCI_KEYS.map((k) => (
              <Key
                key={k.label}
                label={k.label}
                variant="soft"
                title={k.title}
                onPress={() => insert(k.ins)}
              />
            ))}
            <Key label="1/x" variant="soft" title="Kehrwert des Ausdrucks" onPress={reciprocal} />
            <Key label="±" variant="soft" title="Vorzeichen wechseln" onPress={negate} />
            <Key label="%" variant="soft" title="Prozent (durch 100)" onPress={() => insert("%")} />
          </div>

          <div className={s.grid}>
            <Key label="7" onPress={() => insert("7")} />
            <Key label="8" onPress={() => insert("8")} />
            <Key label="9" onPress={() => insert("9")} />
            <Key label="⌫" variant="soft" title="Letztes Zeichen löschen" onPress={backspace} />
            <Key label="C" variant="soft" title="Eingabe löschen" onPress={() => apply("", 0)} />

            <Key label="4" onPress={() => insert("4")} />
            <Key label="5" onPress={() => insert("5")} />
            <Key label="6" onPress={() => insert("6")} />
            <Key label="×" variant="soft" title="Mal" onPress={() => insert("*")} />
            <Key label="÷" variant="soft" title="Geteilt" onPress={() => insert("/")} />

            <Key label="1" onPress={() => insert("1")} />
            <Key label="2" onPress={() => insert("2")} />
            <Key label="3" onPress={() => insert("3")} />
            <Key label="+" variant="soft" title="Plus" onPress={() => insert("+")} />
            <Key label="−" variant="soft" title="Minus" onPress={() => insert("-")} />

            <Key label="0" variant="wide" onPress={() => insert("0")} />
            <Key label="," title="Komma" onPress={() => insert(",")} />
            <Key label="=" variant="eq" title="Berechnen" onPress={submit} />
          </div>

          <div className={s.foot}>
            <span className={s.footHint}>{memory !== null ? `M = ${fmtDisplay(memory)}` : ""}</span>
            <button
              type="button"
              className={mergeClasses(s.copyBtn, copied && s.copyBtnDone)}
              onClick={() => void copy()}
              disabled={result === null}
              title="Ergebnis ohne Tausenderpunkte kopieren"
            >
              {copied ? "Kopiert ✓" : "Kopieren"}
            </button>
          </div>
        </div>
      )}
    </RdPopover>
  );
};

export default CalculatorPopover;
